import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const MyLoanApplications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [open, setOpen] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [selectedLoanSchedule, setSelectedLoanSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [verificationData, setVerificationData] = useState({
    documentsVerified: false,
    verificationComments: '',
  });
  const [nicFilter, setNicFilter] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchLoans();
  }, []);

  const fetchLoans = async () => {
    try {
      const response = await api.get('/officer/loans', {
        params: { officerId: user.userId },
      });
      // Sort loans: APPROVED first (newly approved), then by created date descending
      const sortedLoans = (response.data || []).sort((a, b) => {
        // Newly approved loans first
        if (a.status === 'APPROVED' && b.status !== 'APPROVED') return -1;
        if (a.status !== 'APPROVED' && b.status === 'APPROVED') return 1;
        // Then by created date (newest first)
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
      });
      setLoans(sortedLoans);
    } catch (error) {
      console.error('Error fetching loans:', error);
    }
  };

  const handleVerify = async () => {
    try {
      await api.post(`/officer/loans/${selectedLoan.id}/verify`, null, {
        params: {
          documentsVerified: verificationData.documentsVerified,
          verificationComments: verificationData.verificationComments,
        },
      });
      fetchLoans();
      setOpen(false);
      setSelectedLoan(null);
      setVerificationData({ documentsVerified: false, verificationComments: '' });
    } catch (error) {
      console.error('Error verifying loan:', error);
      alert('Error verifying loan');
    }
  };

  const openDialog = (loan) => {
    setSelectedLoan(loan);
    setVerificationData({
      documentsVerified: loan.documentsVerified || false,
      verificationComments: loan.verificationComments || '',
    });
    setOpen(true);
  };

  const openDetailsDialog = async (loan) => {
    setSelectedLoan(loan);
    setSelectedLoanSchedule(null);
    setLoadingSchedule(true);
    setOpenDetails(true);
    setTabValue(0);
    
    // Fetch loan schedule if loan is ACTIVE or APPROVED
    if (loan.status === 'ACTIVE' || loan.status === 'APPROVED' || loan.status === 'CLOSED') {
      try {
        const response = await api.get(`/officer/loans/${loan.id}/schedule`);
        setSelectedLoanSchedule(response.data);
      } catch (error) {
        console.error('Error fetching loan schedule:', error);
      } finally {
        setLoadingSchedule(false);
      }
    } else {
      setLoadingSchedule(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'VERIFIED':
        return 'info';
      case 'APPROVED':
        return 'success';
      case 'ACTIVE':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'CLOSED':
        return 'default';
      default:
        return 'default';
    }
  };

  // Filter loans by NIC
  const filteredLoans = loans.filter((loan) => {
    if (!nicFilter.trim()) return true;
    const customer = loan.customer;
    const nic = customer?.nic || '';
    return nic.toLowerCase().includes(nicFilter.toLowerCase().trim());
  });

  // Separate loans by status
  const activeLoans = filteredLoans.filter(l => l.status === 'ACTIVE' || l.status === 'APPROVED');
  const rejectedLoans = filteredLoans.filter(l => l.status === 'REJECTED');
  const closedLoans = filteredLoans.filter(l => l.status === 'CLOSED');
  const pendingLoans = filteredLoans.filter(l => l.status === 'PENDING' || l.status === 'VERIFIED');

  const renderLoanTable = (loanList, showActions = true) => {
    if (loanList.length === 0) return null;
    
    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Loan ID</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>NIC</TableCell>
              <TableCell>Requested Amount</TableCell>
              <TableCell>Approved Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created Date</TableCell>
              {showActions && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loanList.map((loan) => (
              <TableRow 
                key={loan.id}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
                onClick={() => openDetailsDialog(loan)}
              >
                <TableCell>{loan.id}</TableCell>
                <TableCell>{loan.customer?.fullName || loan.customer?.name}</TableCell>
                <TableCell>{loan.customer?.nic || 'N/A'}</TableCell>
                <TableCell>Rs {loan.requestedAmount?.toFixed(2)}</TableCell>
                <TableCell>
                  {loan.approvedAmount ? `Rs ${loan.approvedAmount.toFixed(2)}` : 'N/A'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={loan.status}
                    color={getStatusColor(loan.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {loan.createdAt
                    ? new Date(loan.createdAt).toLocaleDateString()
                    : 'N/A'}
                </TableCell>
                {showActions && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {loan.status === 'PENDING' && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openDialog(loan)}
                      >
                        Verify & Submit
                      </Button>
                    )}
                    {(loan.status === 'ACTIVE' || loan.status === 'APPROVED' || loan.status === 'CLOSED') && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate(`/officer/loans/${loan.id}/schedule`)}
                      >
                        View Schedule
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/officer/dashboard')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            color: '#0D47A1',
            fontSize: { xs: '1.75rem', sm: '2rem' },
          }}
        >
          My Loan Applications
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Click on any section below to view loans by status. Each section shows only the relevant loans with their repayment schedules.
      </Alert>

      {/* Loan Status Sections - Clickable Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            onClick={() => navigate('/officer/loans/active')}
            sx={{ 
              background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(46, 125, 50, 0.3)',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: '0 8px 30px rgba(46, 125, 50, 0.5)',
                transform: 'translateY(-4px)',
              },
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Active Loans</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{activeLoans.length}</Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                Click to view all active loans
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            onClick={() => navigate('/officer/loans/rejected')}
            sx={{ 
              background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(211, 47, 47, 0.3)',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: '0 8px 30px rgba(211, 47, 47, 0.5)',
                transform: 'translateY(-4px)',
              },
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Rejected Loans</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{rejectedLoans.length}</Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                Click to view all rejected loans
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            onClick={() => navigate('/officer/loans/closed')}
            sx={{ 
              background: 'linear-gradient(135deg, #616161 0%, #424242 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(97, 97, 97, 0.3)',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: '0 8px 30px rgba(97, 97, 97, 0.5)',
                transform: 'translateY(-4px)',
              },
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Closed Loans</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{closedLoans.length}</Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                Click to view all closed loans
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            onClick={() => navigate('/officer/loans/pending-verified')}
            sx={{ 
              background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(255, 152, 0, 0.3)',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: '0 8px 30px rgba(255, 152, 0, 0.5)',
                transform: 'translateY(-4px)',
              },
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Pending / Verified</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{pendingLoans.length}</Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                Click to view and edit pending/verified loans
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loans.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No loan applications found. Create a new loan application to get started.
        </Alert>
      )}

      {/* Verification Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verify Loan Application</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Loan ID: {selectedLoan?.id}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Customer: {selectedLoan?.customer?.fullName || selectedLoan?.customer?.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Requested Amount: Rs {selectedLoan?.requestedAmount?.toFixed(2)}
            </Typography>
          </Box>
          <TextField
            fullWidth
            label="Verification Comments"
            multiline
            rows={4}
            value={verificationData.verificationComments}
            onChange={(e) =>
              setVerificationData({
                ...verificationData,
                verificationComments: e.target.value,
              })
            }
            margin="normal"
          />
          <Box sx={{ mt: 2 }}>
            <Button
              variant={verificationData.documentsVerified ? 'contained' : 'outlined'}
              onClick={() =>
                setVerificationData({
                  ...verificationData,
                  documentsVerified: !verificationData.documentsVerified,
                })
              }
            >
              {verificationData.documentsVerified
                ? 'Documents Verified ✓'
                : 'Mark Documents as Verified'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleVerify} variant="contained" color="primary">
            Verify & Submit for Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loan Details Dialog */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Loan Details - Loan #{selectedLoan?.id}</Typography>
            <IconButton onClick={() => setOpenDetails(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label="Loan Information" />
            <Tab label="Loan Schedule" disabled={!selectedLoanSchedule} />
          </Tabs>

          {/* Loan Information Tab */}
          {tabValue === 0 && selectedLoan && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Loan Details</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Typography><strong>Loan ID:</strong> {selectedLoan.id}</Typography>
                      <Typography><strong>Status:</strong> 
                        <Chip 
                          label={selectedLoan.status} 
                          color={getStatusColor(selectedLoan.status)}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      <Typography><strong>Requested Amount:</strong> Rs {selectedLoan.requestedAmount?.toFixed(2)}</Typography>
                      <Typography><strong>Approved Amount:</strong> {selectedLoan.approvedAmount ? `Rs ${selectedLoan.approvedAmount.toFixed(2)}` : 'N/A'}</Typography>
                      {selectedLoan.bankDeposit && (
                        <Typography variant="body1" sx={{ mt: 1, mb: 1, p: 1, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 1 }}>
                          <strong>Bank Deposit:</strong> Rs {selectedLoan.bankDeposit.toFixed(2)}
                        </Typography>
                      )}
                      <Typography><strong>Created Date:</strong> {selectedLoan.createdAt ? new Date(selectedLoan.createdAt).toLocaleDateString() : 'N/A'}</Typography>
                      {selectedLoan.disbursementDate && (
                        <Typography><strong>Disbursement Date:</strong> {new Date(selectedLoan.disbursementDate).toLocaleDateString()}</Typography>
                      )}
                      {selectedLoan.loanType && (
                        <Typography><strong>Loan Type:</strong> {selectedLoan.loanType}</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Customer Information</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Typography><strong>Name:</strong> {selectedLoan.customer?.fullName || selectedLoan.customer?.name}</Typography>
                      <Typography><strong>NIC:</strong> {selectedLoan.customer?.nic || 'N/A'}</Typography>
                      <Typography><strong>Phone:</strong> {selectedLoan.customer?.phoneNumber || 'N/A'}</Typography>
                      <Typography><strong>Address:</strong> {selectedLoan.customer?.address || 'N/A'}</Typography>
                      <Typography><strong>District:</strong> {selectedLoan.customer?.district || 'N/A'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                {selectedLoan.notes && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Notes</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedLoan.notes}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Loan Schedule Tab */}
          {tabValue === 1 && (
            <Box>
              {loadingSchedule ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : selectedLoanSchedule ? (
                <>
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>First Installment (Already Deducted):</strong> Rs {selectedLoanSchedule.loan?.loanTemplate?.firstInstallment?.toFixed(2)} - Due on disbursement date
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      The first installment and document charges have been deducted from the loan amount.
                    </Typography>
                  </Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>#</strong></TableCell>
                          <TableCell><strong>Due Date</strong></TableCell>
                          <TableCell><strong>Amount</strong></TableCell>
                          <TableCell><strong>Late Interest</strong></TableCell>
                          <TableCell><strong>Total Due</strong></TableCell>
                          <TableCell><strong>Paid</strong></TableCell>
                          <TableCell><strong>Balance</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Paid Date</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedLoanSchedule.installments
                          ?.filter(inst => inst.installmentNumber > 1)
                          .map((installment) => {
                            const totalDue = parseFloat(installment.totalAmount || installment.amount || 0);
                            const paid = parseFloat(installment.paidAmount || 0);
                            const balance = totalDue - paid;
                            
                            return (
                              <TableRow key={installment.id}>
                                <TableCell>{installment.installmentNumber}</TableCell>
                                <TableCell>
                                  {installment.dueDate ? new Date(installment.dueDate).toLocaleDateString() : 'N/A'}
                                </TableCell>
                                <TableCell>Rs {installment.amount?.toFixed(2) || '0.00'}</TableCell>
                                <TableCell>Rs {installment.lateInterest?.toFixed(2) || '0.00'}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="bold">
                                    Rs {totalDue.toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell>Rs {paid.toFixed(2)}</TableCell>
                                <TableCell>
                                  <Typography 
                                    variant="body2" 
                                    color={balance === 0 ? 'success.main' : balance > 0 ? 'error.main' : 'info.main'}
                                    fontWeight="bold"
                                  >
                                    {balance === 0 ? 'Rs 0.00' : balance > 0 ? `Rs ${balance.toFixed(2)}` : `Rs ${Math.abs(balance).toFixed(2)} (Extra)`}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={installment.status} 
                                    size="small"
                                    color={
                                      installment.status === 'PAID' ? 'success' :
                                      installment.status === 'PARTIAL' ? 'warning' :
                                      installment.status === 'OVERDUE' || installment.status === 'LATE' ? 'error' : 'default'
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  {installment.paidDate ? new Date(installment.paidDate).toLocaleDateString() : '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        {(!selectedLoanSchedule.installments || selectedLoanSchedule.installments.filter(inst => inst.installmentNumber > 1).length === 0) && (
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No installments found for this loan.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Alert severity="info">Loan schedule not available for this loan status.</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Close</Button>
          {selectedLoan && (selectedLoan.status === 'ACTIVE' || selectedLoan.status === 'APPROVED' || selectedLoan.status === 'CLOSED') && (
            <Button 
              variant="contained" 
              onClick={() => {
                setOpenDetails(false);
                navigate(`/officer/loans/${selectedLoan.id}/schedule`);
              }}
            >
              View Full Schedule
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyLoanApplications;
